import { forwardRef, type ComponentProps } from "react";
import {
  Flex as ChakraFlex,
  BoxProps as ChakraBoxProps,
} from "@chakra-ui/react";
import type { FlexSharedProps } from "./Flex.props";

export type FlexProps = FlexSharedProps & {
  // Add any web specific props here.
  as?: ChakraBoxProps["as"]; // couldnt figure out how to recreate this type
};

// Check if a value is an Emotion SerializedStyles object
function isEmotionStyles(value: unknown): boolean {
  return (
    value !== null &&
    typeof value === "object" &&
    "styles" in value &&
    "name" in value
  );
}

// Process css/sx values - convert Emotion SerializedStyles to raw CSS string wrapped in &
function processStyleValue(value: unknown): unknown {
  if (!value) return value;

  if (isEmotionStyles(value)) {
    const { styles } = value as { styles: string };
    return { "&": styles };
  }

  if (Array.isArray(value)) {
    return value.map(processStyleValue);
  }

  return value;
}

const Flex = forwardRef<HTMLDivElement, FlexProps>(function Flex(props, ref) {
  const { sx, css, ...rest } = props;

  // Process css/sx props to handle Emotion SerializedStyles
  const processedCss = processStyleValue(css);
  const processedSx = processStyleValue(sx);

  // Merge sx (v1/v2 legacy) and css props
  const mergedCss =
    processedSx && processedCss
      ? ([processedCss, processedSx] as unknown as ComponentProps<
          typeof ChakraFlex
        >["css"])
      : processedSx ?? processedCss;

  return (
    <ChakraFlex
      ref={ref}
      css={mergedCss}
      {...(rest as unknown as ComponentProps<typeof ChakraFlex>)}
    />
  );
});

export default Flex;
